/**
 * Analytics event helpers for Google Analytics (gtag.js) + Meta Pixel. The base
 * tags load in index.html (initial PageView fires there); these helpers report
 * SPA route changes and the key conversions (sign-up, begin checkout).
 *
 * Every call is guarded so it is a safe no-op during SSR/prerender or when an
 * ad blocker prevented the tags from loading.
 */

/**
 * The GA4 measurement id.
 *
 * Exported ONLY for `gtag('get', …)` reads (see ga-client-id.ts). Nothing may
 * call `gtag('config', …)` with it again — that is the duplicate-configuration
 * anti-pattern removed in `trackPageView`. The id is configured once, in
 * index.html.
 */
import { isInternalViewer } from './internal-viewer';
import { hasConsent } from './cookie-consent';
import { countryConfig } from '../country';
import type { PaymentType } from './payment-type';

export const GA4_MEASUREMENT_ID = countryConfig.analytics.ga4MeasurementId;

// Staff / test sessions send NOTHING to Meta or GA — see internal-viewer.ts.
/**
 * Crawlers that EXECUTE our JavaScript and would otherwise fire the Pixel like a
 * visitor. Measured 2026-09-30: Meta's own `meta-externalagent` produced 11,530
 * browser ViewContent events against a normal ~2,000 a day. Named agents only —
 * deliberately no generic `bot` substring, which a real phone can carry
 * («CUBOT»): a missed crawler costs a few junk events, a muted buyer costs a
 * conversion. The API bridge (`POST /meta/events`) drops the same callers
 * server-side with the broader list, where a false positive loses nothing.
 */
const CRAWLER_AGENT =
  /meta-externalagent|facebookexternalhit|facebookcatalog|HeadlessChrome|Googlebot|bingbot|YandexBot|GPTBot|OAI-SearchBot|ClaudeBot|PerplexityBot|Bytespider|AhrefsBot|SemrushBot|PetalBot|Applebot/i;

export function isCrawlerAgent(userAgent?: string): boolean {
  const ua = userAgent ?? (typeof navigator === 'undefined' ? '' : navigator.userAgent);
  return CRAWLER_AGENT.test(ua);
}

// Cookie consent (`features.cookieConsent`): the Pixel only under a «publicitarias»
// grant, GA4 only under an «analíticas» one. Constant `true` where consent is not
// required (UZ/KZ) — see lib/cookie-consent.ts.
function hasFbq(): boolean {
  return (
    !isInternalViewer() && !isCrawlerAgent() && hasConsent('marketing') && typeof fbq === 'function'
  );
}

/*
 * PIXEL IDENTITY IS SET IN index.html, NOT HERE.
 *
 * The pixel adopts user data only while its stored userData is still empty and
 * answers any later `init` for the same pixel id with a DUPLICATE_PIXEL_ID
 * user error — there is no merge. So exactly one `init` can ever carry
 * identity, and it must be the one that runs before the initial PageView.
 *
 * FOUR functions used to live here and all of them are gone:
 *
 *  - `initPixelIdentity()` seeded `external_id` from the app bundle. Its job
 *    moved into index.html, where it now runs before the first PageView rather
 *    than after it.
 *  - `identifyUser()` re-inited with `{em, fn, ln}` after login. It never
 *    worked: by then userData was non-empty, so every call was rejected. It
 *    was removed rather than fixed — email reaches Meta on the Conversions API
 *    leg of every server-authored event (registration, checkout, purchase,
 *    ViewContent, Search), so the browser copy adds nothing that Meta does not
 *    already receive, and getting it there would mean persisting a hash of the
 *    user's email in localStorage where every script on the origin can read it.
 *  - `setMetaUserData()` / `clearMetaUserData()` (shipped AND removed
 *    2026-09-26) called `fbq('set', 'userData', …)`, which Meta's own docs
 *    describe as the way to UPDATE Advanced Matching after `init`. MEASURED the
 *    same day against production, headless Chromium: **fbevents.js rejects the
 *    call.** Every page load printed
 *    `[Meta Pixel] - Call to "fbq('set', 'userData', Object);" with parameter
 *    "pixel_id" has an invalid value of "'<pixel id>'"`, and the 4-argument
 *    form `fbq('set','userData', data, '<pixel id>')` was rejected with the
 *    same message. After the call `fbq.getState().pixels[0].userData` still
 *    held exactly `{external_id: <visitor id from the index.html init>}` —
 *    nothing was attached. So it was a no-op that spammed the console of every
 *    visitor, and it was deleted rather than reshaped.
 *
 * Which leaves exactly ONE way to give this pixel an identity: the single
 * `init` in index.html, before the first PageView. The browser leg therefore
 * carries `external_id` (the anonymous `tenders_vid`) plus `_fbp`/`_fbc` and
 * nothing else; `em`/`ph`/`fn`/`ln` reach Meta only on the server (CAPI) leg,
 * which is unchanged.
 *
 * See docs/explanation/meta-website-audience-empty.md.
 */

function hasGtag(): boolean {
  return !isInternalViewer() && hasConsent('analytics') && typeof gtag === 'function';
}

/**
 * Fire the Meta Pixel PageView on SPA route changes.
 *
 * GA4 is deliberately NOT called here. This used to run
 * `gtag('config', GA4_ID, { page_path })` on every route change, which was
 * wrong three times over:
 *
 *  - `page_path` is a Universal Analytics parameter. GA4 documents
 *    `page_location` and `page_title`; `page_path` appears in neither the GA4
 *    pageview reference nor the gtag.js parameter reference, so the one thing
 *    the call claimed to communicate was never read.
 *  - GA4 enhanced measurement listens for pushState/popState/replaceState by
 *    default, and `createBrowserRouter` navigates via pushState — so GA4 was
 *    already counting these navigations on its own.
 *  - Repeating `config` for the same measurement id is the duplicate-
 *    configuration anti-pattern Google warns produces double-counted views.
 *
 * So the call was either silently discarded (if the stream has "ignore
 * duplicate instances of on-page configuration" enabled) or doubling every SPA
 * pageview. Deleting it is correct under both, whereas "fixing" `page_path`
 * would have converted a harmless no-op into a real double-count.
 *
 * Meta genuinely does need the manual call — its pixel has no SPA awareness.
 */
export function trackPageView(_path: string): void {
  if (hasFbq()) fbq('track', 'PageView');
}

/**
 * Fire the sign-up conversion: Meta CompleteRegistration + GA4 sign_up.
 * `eventId` is the dedup id shared with the server-side CAPI event.
 */
export function trackCompleteRegistration(eventId: string): void {
  if (hasFbq()) fbq('track', 'CompleteRegistration', {}, { eventID: eventId });
  if (hasGtag()) gtag('event', 'sign_up', { method: 'email' });
}

/**
 * Fire the «указал ИНН компании» conversion: GA4 `generate_lead`.
 *
 * GA4 ONLY, on purpose. Meta receives this step as `Lead` from the server
 * (`POST /auth/me/company` → `sendLeadEvent`) and has no browser copy; adding a
 * Pixel `Lead` here without the server's event id would count every ИНН twice.
 *
 * GA4 had no copy of it at all, and since 2026-10-04 Google Ads takes its
 * conversions from this GA4 property. An ИНН is the step that separates buyers
 * from the rest: 12 % of accounts that entered one paid against 1.5 % of those
 * that did not (registrations up to 20.09.2026, measured 2026-10-04) — so it is
 * the one early signal worth handing to a bidding system.
 */
export function trackLead(): void {
  if (hasGtag()) gtag('event', 'generate_lead');
}

/**
 * Fire the `/zayavka` lead: Meta `Lead`, `content_name: 'zayavka'` — the event
 * the Meta Lead campaign optimises on (owner, 2026-10-06).
 *
 * Unlike the ИНН `Lead` above (server-only), this one HAS a browser leg: the
 * visitor is anonymous ad traffic, and the Pixel cookies + the live click are
 * what attribute the lead to its ad. `eventId` is the one the page sent to
 * `POST /leads`, whose CAPI twin carries the hashed phone — Meta collapses the
 * pair into one lead. The page calls this only AFTER the server accepted the
 * lead, and not at all for a same-day repeat of the phone (the server sends no
 * twin then either), so one person is one Lead.
 *
 * GA4 deliberately gets nothing here: `generate_lead` already means «указал
 * ИНН», and Google Ads reads its conversions from this GA4 property — a second
 * meaning under the same name would change what that signal counts.
 */
export function trackLeadForm(eventId: string): void {
  if (hasFbq()) fbq('track', 'Lead', { content_name: 'zayavka' }, { eventID: eventId });
}

/**
 * Fire the begin-checkout conversion: Meta InitiateCheckout + GA4 begin_checkout.
 * `eventId` is the dedup id shared with the server-side CAPI event.
 *
 * ⚠️ NO `value`/`currency` to Meta (owner's decision, 2026-10-03). They were
 * sent from 2026-09-02 because Events Manager flags their absence, and the
 * figure was honest about the PLAN — but not about the PERSON: a week of
 * 26.09–02.10 carried 913 InitiateCheckout events at 399 000 soʼm each against
 * 16 payments, and most of the people behind them were refused by their bank
 * for insufficient funds at the charge. A checkout that was never going to be
 * paid is not worth the plan's price, and a value on it says so to anyone who
 * switches a campaign to value bidding. The Events Manager warning is the
 * accepted cost. Value lives on Subscribe/Purchase, where money actually moved.
 * GA4 keeps the value: it is a reporting figure there, not a bidding input.
 *
 * `paymentType` rides as `payment_type` (annual / subscription / onetime — see
 * `payment-type.ts`), and the caller hands `sendMetaEvent` the SAME value for
 * the CAPI twin: a deduplicated pair keeps one leg's custom_data, so the legs
 * must agree. Required, so no firing site can forget it.
 */
export function trackInitiateCheckout(
  eventId: string,
  params: { value: number; currency: string; planKey: string; paymentType: PaymentType },
): void {
  if (hasFbq()) {
    fbq(
      'track',
      'InitiateCheckout',
      { content_name: params.planKey, payment_type: params.paymentType },
      { eventID: eventId },
    );
  }
  if (hasGtag()) {
    gtag('event', 'begin_checkout', {
      value: params.value,
      currency: params.currency,
      items: [{ item_id: params.planKey }],
    });
  }
}

/**
 * Fire the "this buyer has started paying" conversion: Meta AddPaymentInfo +
 * GA4 `add_payment_info`. `eventId` is the id of the checkout the payment sheet
 * minted — the SAME one that checkout's InitiateCheckout carries.
 *
 * ⚠️ Meta's AddPaymentInfo used to be deliberately skipped here, on the
 * argument that an ad set needs ~50 optimisation events a week and this rung
 * sits below `checkout_started`. That was an argument about what to OPTIMISE
 * on, and it left the account with no honest column at all. InitiateCheckout
 * still fires when a MEMBER's payment sheet OPENS: 16–22.09 members opened
 * sheets 388 times (145 people) against 54 typed card numbers, 21 prepared
 * pay-link clicks, 122 `checkout_started` and 32 payments. So IC reads as
 * "looked at the sheet"; this reads as "started paying". IC is left alone —
 * the running CBO campaign optimises on its volume — and the campaign can be
 * moved onto this event once it carries the weekly volume to leave learning.
 *
 * ⚠️ Sharing the IC's `eventId` is not a collision. An event id dedups a Pixel
 * event against the CAPI copy of the SAME event name; two different names never
 * dedup with each other. So this dedups against its own CAPI twin and IC's
 * count does not move.
 *
 * GA4's copy of this standard step used to fire one rung later, from the card
 * sheet's submit handler, which is why it never existed for the one-time or
 * Octobank flows. It fires HERE now, once per sheet opening, for all three.
 *
 * `paymentType` → `payment_type`, on the same terms as `trackInitiateCheckout`.
 */
export function trackAddPaymentInfo(
  eventId: string,
  params: { value: number; currency: string; planKey: string; paymentType: PaymentType },
): void {
  if (hasFbq()) {
    fbq(
      'track',
      'AddPaymentInfo',
      // No value, for the reason given on `trackInitiateCheckout`.
      { content_name: params.planKey, payment_type: params.paymentType },
      { eventID: eventId },
    );
  }
  if (hasGtag()) {
    gtag('event', 'add_payment_info', {
      // Matches `begin_checkout` and what the buyer is actually charged.
      value: params.value,
      currency: params.currency,
      items: [{ item_id: params.planKey }],
    });
  }
}

/**
 * Fire a Meta ViewContent event — used on the pricing/tariff page so we can
 * measure "reached the tariff page" and build an audience of price-considerers.
 *
 * `eventId` is the dedup id shared with the server-side CAPI event, which the
 * caller mirrors via `sendMetaEvent`. Unlike the other conversions this one
 * fires for anonymous visitors, so the CAPI copy is what keeps ad-blocked
 * traffic visible to Meta — send BOTH or coverage regresses.
 */
export function trackViewContent(contentName: string, eventId: string): void {
  if (hasFbq()) {
    fbq('track', 'ViewContent', { content_name: contentName }, { eventID: eventId });
  }
  if (hasGtag()) gtag('event', 'view_item_list', { item_list_id: contentName });
}

/**
 * Fire a Meta Search event when a visitor runs a catalog query.
 *
 * `search_performed` has been recorded first-party for a long time and never
 * forwarded to Meta, so an optimisable standard event was being observed and
 * thrown away. `eventId` dedups against the CAPI copy sent via `sendMetaEvent`
 * — send BOTH, or ad-blocked visitors regress out of coverage.
 */
export function trackSearch(query: string, eventId: string): void {
  if (hasFbq()) fbq('track', 'Search', { search_string: query }, { eventID: eventId });
  if (hasGtag()) gtag('event', 'search', { search_term: query });
}

/**
 * Fire the activation conversion when a user saves a search (turns on alerts).
 * Custom Meta event + GA4 event. `eventId` dedups against the server-side CAPI
 * ActivationSavedSearch event.
 */
export function trackSavedSearchActivation(eventId: string): void {
  if (hasFbq()) fbq('trackCustom', 'ActivationSavedSearch', {}, { eventID: eventId });
  if (hasGtag()) gtag('event', 'activation_saved_search');
}

/**
 * Fire the CONFIRMED-PURCHASE conversion from the browser: Meta
 * Purchase/Subscribe, on the payment return page.
 *
 * The server has always sent this leg from the Payme callback, where there is no
 * browser context — so `Subscribe` reached Meta as "Conversions API only" and
 * was attributed to an ad click ZERO times in the account's history, while
 * `CompleteRegistration` (browser + server) attributed at ~23%. The browser copy
 * carries the live `_fbc`/`_fbp` cookies the server call cannot see.
 *
 * ⚠️ `eventName` and `eventId` come from `GET /billing/conversion` and must be
 * passed through UNCHANGED. The id is `subscribe-<orderId>` / `purchase-<orderId>`
 * minted by `paymentEventId` server-side; reconstruct it here and any drift
 * makes Meta count one purchase twice instead of collapsing the pair. Annual is
 * `Purchase`, monthly is `Subscribe` — the server picks, we echo.
 *
 * GA4 `purchase` IS sent from here too (2026-10-04) — see `trackGaPurchase`
 * below for why the earlier «server leg only» rule left GA4 with no purchase
 * at all.
 *
 * ⚠️ This leg carries NO `em`/`ph`/`fn`/`ln` and cannot be made to. On
 * 2026-09-26 it briefly applied `fbq('set','userData', …)` itself, immediately
 * before the `track` — fbevents.js rejects that call outright (see the identity
 * note at the top of this file), so the attach never happened and the code was
 * removed. What it does carry is `external_id` from the index.html `init`, plus
 * `_fbp` and the re-seated `_fbc` below, plus the IP/UA of the request. Since
 * Meta keeps the FIRST event of a deduped pair and does not merge `user_data`
 * across the two legs, this browser copy — not the server's richer CAPI copy —
 * is the one that usually wins the pair. Whether that costs anything is a
 * question for the Subscribe Event Match Quality in Events Manager, not for the
 * code; if EMQ says it does, the fix is to stop sending the browser leg for
 * Subscribe/Purchase, not to retry an API the pixel does not implement.
 *
 * `paymentType` → `payment_type` (annual / subscription / onetime), echoed from
 * the server like everything else here: the server leg derives it from the
 * order's rail, and a pair keeps one leg's custom_data — so if the leg kept is
 * this one, it has to say what the server said. Optional because the web and
 * the API ship on separate pipelines; an API that does not send it yet means
 * this leg omits the key rather than inventing a value.
 */
export function trackPurchaseConfirmed(params: {
  eventName: 'Purchase' | 'Subscribe';
  eventId: string;
  valueUzs: number;
  currency: string;
  planKey: string;
  paymentType?: PaymentType | null;
  fbc?: string | null;
}): void {
  // GA4 first and independently: a blocked or absent Pixel must not cost Google
  // the purchase, and nothing in the GA4 leg can throw into the Meta one.
  trackGaPurchase(params);
  if (!hasFbq()) return;
  // Re-seat `_fbc` from the value the server captured at checkout BEFORE firing:
  // the buyer returns from Payme's hosted page on a fresh load where the live
  // cookie is usually gone, so without this the Subscribe/Purchase browser leg
  // reaches Meta with no click id at all (fbc coverage 0% — the worst of every
  // conversion). The Pixel reads `_fbc` from the cookie at `track()` time, and a
  // `document.cookie` write is synchronous, so this lands on the same event.
  reseatFbcCookie(params.fbc);
  fbq(
    'track',
    params.eventName,
    {
      value: params.valueUzs,
      currency: params.currency,
      content_name: params.planKey,
      ...(params.paymentType ? { payment_type: params.paymentType } : {}),
    },
    { eventID: params.eventId },
  );
}

/** One flag per order: the card modal and `/billing/return` both report it. */
const GA_PURCHASE_SENT_PREFIX = 'uzt:ga-purchase:';

/**
 * The GA4 `purchase` for a server-confirmed payment.
 *
 * WHY THE BROWSER SENDS IT (2026-10-04). The rule used to be «GA4 gets
 * `purchase` from the server only» (`Ga4MeasurementService`, Measurement
 * Protocol). That leg needs `GA4_API_SECRET`, production never had one, and so
 * GA4 received NO purchase at all — 0 of 60 completed orders carried
 * `ga4_event_sent_at` on 2026-08-16, and nothing changed since. Google Ads
 * reads its conversions from this GA4 property, so without a purchase here a
 * search campaign could only ever be judged on registrations.
 *
 * `transaction_id` is the PaymentOrder id — the tail of the server's
 * `subscribe-<orderId>` / `purchase-<orderId>` event id, and exactly what the
 * server leg would send. GA4 collapses repeated `transaction_id`s from one
 * user, so if `GA4_API_SECRET` is ever set the two legs describe one purchase,
 * not two. ⚠️ Check that in GA4 (Monetisation → Transactions) the day it is
 * set, rather than trusting this sentence.
 *
 * All four of `transaction_id`, `value`, `currency` and `items` are required:
 * miss one and GA4 files the event as a custom event that no ecommerce report
 * and no Google Ads import ever sees. UZS is on GA4's currency list.
 *
 * Sent ONCE per order per browser. The modal and the return page both call
 * `trackPurchaseConfirmed` by design (Meta dedups on the event id); the
 * localStorage flag keeps GA4 to a single hit without relying on its dedup,
 * and when storage is unavailable that dedup is the fallback.
 */
function trackGaPurchase(params: {
  eventId: string;
  valueUzs: number;
  currency: string;
  planKey: string;
}): void {
  if (!hasGtag()) return;
  const transactionId = params.eventId.replace(/^(?:subscribe|purchase)-/, '');
  try {
    const key = GA_PURCHASE_SENT_PREFIX + transactionId;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
  } catch {
    /* storage unavailable (private mode) — GA4's transaction_id dedup covers a repeat. */
  }
  gtag('event', 'purchase', {
    transaction_id: transactionId,
    value: params.valueUzs,
    currency: params.currency,
    items: [
      {
        item_id: params.planKey,
        item_name: params.planKey,
        price: params.valueUzs,
        quantity: 1,
      },
    ],
  });
}

/**
 * Write a stored `_fbc` (Meta's `fb.1.<ms>.<fbclid>` shape) into the cookie so
 * the Pixel attaches it — but ONLY when no live `_fbc` exists, so a same-browser
 * ad-clicker's fresher cookie always wins. Host-scoped (no `domain=`): this leg
 * only needs the value readable on the current page, and a bare host cookie is
 * safe on localhost too. Mirrors the document-start capture in index.html.
 */
function reseatFbcCookie(fbc?: string | null): void {
  if (!fbc || !fbc.startsWith('fb.')) return;
  try {
    if (/(^|;\s*)_fbc=/.test(document.cookie)) return; // live cookie wins
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `_fbc=${fbc}; path=/; max-age=7776000${secure}; SameSite=Lax`;
  } catch {
    /* cookie unavailable (sandboxed / very old browser) — skip. */
  }
}

/**
 * Tell GA4 which person this is, so it stops counting one human as several.
 *
 * GA4 has run device-scoped since day one — no `user_id` was ever set. On
 * ~99% mobile traffic the volume impact is modest, but the CONVERSION impact is
 * not: someone who clicks an ad on a phone and pays on a laptop is two GA4
 * users, and no report can join them. It is also the fallback identity that
 * lets a server-sent `purchase` stitch to the browsing that preceded it.
 *
 * `users.id` is a cuid — not an email, not PII, well under GA4's 256-char cap.
 *
 * Never called with an empty string: Google warns a blank or dummy id causes
 * permanent data loss, so the caller passes `null` on logout instead, which is
 * the documented way to clear it.
 */
export function setGaUserId(userId: string | null): void {
  if (!hasGtag()) return;
  gtag('set', { user_id: userId });
}
